import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useMemo, useRef } from 'react'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { getEventProperties } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'

export enum ImagePreviewSource {
  IMAGE_URL,
  DESIGN,
  NONE,
  FAIL,
  EMPTY,
  TEXT,
  LOADING,
}

export type PreviewImageState = {
  previewSource: ImagePreviewSource
  showLoading: boolean
}

type Input = {
  isDesignLoaded: boolean
  didGenerationFail?: boolean
  event: AlmanacFileCalendarEvent
  isImageLoaded: boolean
  isCopilot?: boolean
  calendarV2?: boolean
  showRenderingImageOverlay?: boolean
}

export const usePreviewImageState = ({
  event,
  isImageLoaded,
  isDesignLoaded,
  didGenerationFail = false,
  isCopilot = false,
  calendarV2 = false,
  showRenderingImageOverlay = false,
}: Input): PreviewImageState => {
  const hasRenderedDesignContentRef = useRef(false)
  const { isMobile } = useResponsiveDesign()

  const state = useMemo<PreviewImageState>(() => {
    const {
      empty,
      image_url: imageUrl,
      is_generating,
      image_url_generating,
      design_content,
      width,
      height,
      content,
      is_design_blank,
      is_loading,
    } = event

    const { isLoading, isLoadingDocPreview, isWithinPreviewImageWaitTime, showDocText } = getEventProperties(
      event,
      isImageLoaded,
      isDesignLoaded,
      isCopilot,
      calendarV2,
    )

    if (didGenerationFail) {
      return { previewSource: ImagePreviewSource.FAIL, showLoading: false }
    }

    if (
      !imageUrl &&
      !design_content &&
      !empty &&
      !is_generating &&
      !image_url_generating &&
      !content &&
      !(calendarV2 && is_loading)
    ) {
      return { previewSource: ImagePreviewSource.NONE, showLoading: false }
    }

    if (imageUrl && (calendarV2 || !hasRenderedDesignContentRef.current) && !is_design_blank) {
      return {
        previewSource: ImagePreviewSource.IMAGE_URL,
        showLoading: calendarV2 ? !!is_loading || showRenderingImageOverlay : !isImageLoaded,
      }
    }

    if (!calendarV2 && design_content && !isMobile && width && height && !is_design_blank) {
      hasRenderedDesignContentRef.current = true
      return {
        previewSource: ImagePreviewSource.DESIGN,
        showLoading: !isDesignLoaded,
      }
    }

    if (showDocText) {
      return { previewSource: ImagePreviewSource.TEXT, showLoading: false }
    }

    if (calendarV2 && is_loading) {
      return { previewSource: ImagePreviewSource.LOADING, showLoading: true }
    }
    if (isWithinPreviewImageWaitTime && (isLoading || isLoadingDocPreview)) {
      return { previewSource: ImagePreviewSource.LOADING, showLoading: true }
    }

    return { previewSource: ImagePreviewSource.EMPTY, showLoading: false }
  }, [
    event,
    isImageLoaded,
    isDesignLoaded,
    isCopilot,
    didGenerationFail,
    isMobile,
    calendarV2,
    showRenderingImageOverlay,
  ])

  return state
}
